import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import "../documents/Documents.css";
import "./Attendance.css";
import AttendanceClockModal from "./AttendanceClockModal";
import { AttendanceGuideButton, AttendanceHelpModal } from "./AttendanceHelp";
import {
  clockAttendance,
  detectClientDeviceType,
  getRequireUserLocationForOffice,
  handleAttendanceApiError,
  hasOpenSegment,
  inferNextClockEventType,
  lastEvent,
  sumClosedHours,
} from "./attendanceApi";
import { loadTodayActivity, setTodayActivity } from "./attendanceActions";
import { emptyTodayActivity } from "./attendanceReducer";
import { getCurrentEmployee, getSelectedOffice } from "../../Global/CustomFunctions";
import { fetchTimesheetProjects, mapTimesheetProjectsResponse } from "../timesheets/timesheetApi";
import { AttendanceEventType, STANDARD_DAILY_WORK_HOURS } from "./attendanceConstants";
import {
  formatAttendanceDateLong,
  formatAttendanceTime,
  formatHoursShort,
  getAttendanceDisplayTimeZone,
  parseUtcFromApi,
} from "./attendanceDateUtils";
import { Common } from "../../../utils";
import { bypassEmployeeEditDeleteOfficeFlags, displayAttendancePlaceLabel } from "./attendanceDailyLogsUtils";
import { useWorkplaceGate } from "../../../hooks/useWorkplaceGate";

const deviceLabel = (t, clientDeviceType) => {
  const n = Number(clientDeviceType);
  if (n === 2) return t("app.attendance.hub.platformMobile");
  if (n === 3) return t("app.attendance.hub.platformTablet");
  if (n === 1) return t("app.attendance.hub.platformDesktop");
  if (n === 4) return t("app.attendance.hub.platformWeb");
  if (n === 6) return t("app.attendance.hub.platformBiometric");
  return t("app.attendance.hub.platformOther");
};

const AttendanceHubPage = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { withWorkplace, workplaceRequiredModal } = useWorkplaceGate("attendance");
  const [now, setNow] = useState(() => new Date());
  const today = useSelector((state) => state.attendanceReducer?.today);
  const loading = useSelector((state) => state.attendanceReducer?.loading);
  const [clockLoading, setClockLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [modalMode, setModalMode] = useState(/** @type {'checkIn'|'checkOut'} */ ("checkIn"));
  const [requireUserLocation, setRequireUserLocation] = useState(false);
  const [helpKey, setHelpKey] = useState(null);

  const loadToday = useCallback(
    async ({ silent = false } = {}) => {
      try {
        await dispatch(loadTodayActivity({ silent }));
      } catch (e) {
        if (!silent) {
          handleAttendanceApiError(e, t("app.attendance.errors.loadToday"));
          dispatch(setTodayActivity(emptyTodayActivity()));
        }
      }
    },
    [dispatch, t]
  );

  useEffect(() => {
    loadToday();
  }, [loadToday]);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        loadToday({ silent: true });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [loadToday]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sel = getSelectedOffice();
      const oid = sel && typeof sel === "object" ? sel.id ?? sel.Id : null;
      const officeId = oid != null ? Number(oid) : NaN;
      const required = await getRequireUserLocationForOffice(officeId);
      if (!cancelled) setRequireUserLocation(required);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = await fetchTimesheetProjects();
        const list = mapTimesheetProjectsResponse(raw);
        if (!cancelled) setProjects(list);
      } catch {
        if (!cancelled) setProjects([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const segments = today?.segments ?? [];
  const events = today?.events ?? [];
  const hidePunctuality = Boolean(today?.allowFlexibleWorkingWindow);
  const lastEv = lastEvent(events);
  const open = lastEv
    ? lastEv.eventType === AttendanceEventType.CheckIn
    : hasOpenSegment(segments);
  const nextType = inferNextClockEventType(segments, events);
  const totalClosed = sumClosedHours(segments);
  const openElapsedHours = useMemo(() => {
    if (!open) return 0;
    const le = lastEvent(events);
    if (!le || le.eventType !== AttendanceEventType.CheckIn || !le.occurredAtUtc) return 0;
    const start = parseUtcFromApi(le.occurredAtUtc)?.getTime();
    if (start == null) return 0;
    return Math.max(0, (now.getTime() - start) / 3600000);
  }, [open, events, now]);
  const displayTotal = totalClosed + openElapsedHours;

  /** Same rule as Daily Logs: API overtime, else max(0, total − 8h) using hub total (closed + open session). */
  const overtimeDisplay = useMemo(() => {
    const apiOt = today?.overtimeHours;
    if (apiOt != null && Number.isFinite(Number(apiOt))) return Number(apiOt);
    const total = totalClosed + openElapsedHours;
    if (!Number.isFinite(total)) return null;
    return Math.max(0, total - STANDARD_DAILY_WORK_HOURS);
  }, [today?.overtimeHours, totalClosed, openElapsedHours]);

  /** Earliest check-in today (punctuality vs expected start). */
  const firstCheckIn = useMemo(() => {
    const ins = events.filter((e) => e.eventType === AttendanceEventType.CheckIn);
    if (!ins.length) return null;
    return [...ins].sort(
      (a, b) =>
        (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0) - (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0)
    )[0];
  }, [events]);

  /** First clock-in vs 9:00 in display timezone (simple on-time / late). Hidden when flexible working window is on. */
  const punctualityKind = useMemo(() => {
    if (hidePunctuality) return null;
    if (!firstCheckIn?.occurredAtUtc) return null;
    const d = parseUtcFromApi(firstCheckIn.occurredAtUtc);
    if (!d) return null;
    const tz = getAttendanceDisplayTimeZone();
    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(d);
    const hour = Number(parts.find((p) => p.type === "hour")?.value ?? NaN);
    const minute = Number(parts.find((p) => p.type === "minute")?.value ?? NaN);
    if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
    const mins = hour * 60 + minute;
    const expectedStart = 9 * 60;
    return mins <= expectedStart ? "onTime" : "late";
  }, [firstCheckIn, hidePunctuality]);

  /** Open session: current check-in is the last event (must be CheckIn). */
  const activeCheckIn = useMemo(() => {
    if (!open || !events.length) return null;
    const le = lastEvent(events);
    if (le && le.eventType === AttendanceEventType.CheckIn) return le;
    return null;
  }, [open, events]);

  const primaryAction = useMemo(() => {
    if (nextType === AttendanceEventType.CheckOut) return "checkout";
    return "checkin";
  }, [nextType]);

  useEffect(() => {
    if (!modalOpen) return;
    if (modalMode === "checkIn" && open) setModalOpen(false);
    if (modalMode === "checkOut" && !open) setModalOpen(false);
  }, [open, modalMode, modalOpen]);

  const openClockModal = (mode) => {
    withWorkplace(() => {
      setModalMode(mode);
      setModalOpen(true);
    });
  };

  const handleModalConfirm = async (payload) => {
    setClockLoading(true);
    try {
      const dev = detectClientDeviceType();
      const sel = getSelectedOffice();
      const officeRaw = sel && typeof sel === "object" ? sel.id ?? sel.Id : null;
      const officeId = officeRaw != null ? Number(officeRaw) : NaN;
      let departmentId = NaN;
      const emp = getCurrentEmployee();
      if (emp && typeof emp === "object") {
        const d = emp.departmentId ?? emp.DepartmentId;
        if (d != null && d !== "") departmentId = Number(d);
      }
      const result = await clockAttendance({
        ...payload,
        clientDeviceType: dev,
        officeId: Number.isFinite(officeId) && officeId > 0 ? officeId : undefined,
        departmentId: Number.isFinite(departmentId) && departmentId > 0 ? departmentId : undefined,
      });
      Common.showSuccessMessage(
        payload.eventType === AttendanceEventType.CheckOut
          ? t("app.attendance.hub.checkedOut")
          : t("app.attendance.hub.checkedIn")
      );
      setModalOpen(false);
      if (result?.todayActivity) {
        dispatch(setTodayActivity(result.todayActivity));
      } else {
        await loadToday({ silent: true });
      }
    } catch (e) {
      handleAttendanceApiError(e, t("app.attendance.errors.clock"));
    } finally {
      setClockLoading(false);
    }
  };

  return (
    <div className="px-3 px-md-4 py-4">
      <div className="att-attendance-route">
        <CommonPageHeader
          icon={<i className="bi bi-clock-history" />}
          title={t("app.attendance.hub.title")}
          description={t("app.attendance.hub.subtitle")}
          rightContent={<AttendanceGuideButton onClick={() => setHelpKey("hub")} />}
        />

        <div className="att-page">
        <div className="att-hub-bento">
          <section className="att-hero att-hero--light">
            <div className="position-relative" style={{ zIndex: 1 }}>
              <div className="d-flex justify-content-between align-items-start">
                <p className="att-hero-eyebrow mb-0">
                  <span className="att-hero-eyebrow-dot" />
                  {open ? t("app.attendance.hub.activeSession") : t("app.attendance.hub.noOpenSession")}
                </p>
                <i className="bi bi-broadcast-pin att-hero-pin d-none d-md-block" aria-hidden />
              </div>
              <p className="att-hero-time mb-0">{formatAttendanceTime(now, { withSeconds: true })}</p>
              <p className="att-hero-date mb-0">{formatAttendanceDateLong(now)}</p>
              <p className="att-hero-timezone-hint mb-0">
                {t("app.attendance.hub.displayTimeZone", { tz: getAttendanceDisplayTimeZone() })}
              </p>
            </div>
            <div className="att-hero-footer">
              <div>
                <div className="att-chip-row">
                  <span className="att-chip">{open ? t("app.attendance.hub.present") : t("app.attendance.hub.offClock")}</span>
                  <span className="att-chip">{t("app.attendance.hub.office")}</span>
                </div>
                {open && activeCheckIn && (
                  <div className="att-hero-checkin-line">
                    <span className="att-activity-icon att-activity-icon--in" style={{ width: 36, height: 36 }}>
                      <i className="bi bi-box-arrow-in-right" />
                    </span>
                    <span>
                      {t("app.attendance.hub.checkedInAt")}{" "}
                      <strong>{formatAttendanceTime(activeCheckIn.occurredAtUtc)}</strong>
                    </span>
                  </div>
                )}
              </div>
              <button type="button" className="att-btn att-btn--checkout" onClick={() => openClockModal(primaryAction === "checkout" ? "checkOut" : "checkIn")}>
                {primaryAction === "checkout" ? t("app.attendance.hub.checkOutCta") : t("app.attendance.hub.checkInCta")}
              </button>
            </div>
          </section>

          <div className="att-metric-stack">
            <div className="att-metric-card">
              <div>
                <p className="att-metric-label mb-0">{t("app.attendance.hub.totalHours")}</p>
                <p className="att-metric-value mb-0 d-flex align-items-center flex-wrap gap-2">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                  ) : (
                    <>
                      {formatHoursShort(displayTotal)}
                      <span className="text-muted fs-6">hrs</span>
                    </>
                  )}
                </p>
              </div>
              <div className="att-metric-icon" style={{ background: "rgba(49,46,129,0.08)", color: "var(--att-accent-indigo)" }}>
                <i className="bi bi-hourglass-split" />
              </div>
            </div>
            <div className="att-metric-card">
              <div>
                <p className="att-metric-label mb-0">{t("app.attendance.hub.status")}</p>
                <p className="att-metric-value mb-0 text-success d-flex align-items-center">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                  ) : open ? (
                    t("app.attendance.hub.present")
                  ) : (
                    t("app.attendance.hub.notPresent")
                  )}
                </p>
              </div>
              <div className="att-metric-icon" style={{ background: "rgba(0,100,68,0.1)", color: "var(--att-tertiary)" }}>
                <i className="bi bi-check2-circle" />
              </div>
            </div>
            <div className="att-metric-card">
              <div>
                <p className="att-metric-label mb-0">{t("app.attendance.hub.overtime")}</p>
                <p className="att-metric-value mb-0 d-flex align-items-center flex-wrap gap-2">
                  {loading ? (
                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                  ) : overtimeDisplay != null && Number.isFinite(overtimeDisplay) ? (
                    <>
                      {formatHoursShort(overtimeDisplay)}
                      <span className="text-muted fs-6">hrs</span>
                    </>
                  ) : (
                    "—"
                  )}
                </p>
              </div>
              <div className="att-metric-icon" style={{ background: "#fff7ed", color: "var(--att-secondary)" }}>
                <i className="bi bi-graph-up-arrow" />
              </div>
            </div>
          </div>
        </div>

        <div className="att-split">
          <div>
            <div className="att-section-head">
              <div>
                <h3 className="att-section-title">{t("app.attendance.hub.activityTitle")}</h3>
                <p className="att-section-sub">{t("app.attendance.hub.activitySubtitle")}</p>
              </div>
              <button type="button" className="att-link-btn" onClick={() => history.push("/attendance/daily-logs")}>
                {t("app.attendance.hub.fullHistory")}
              </button>
            </div>
            <div className="att-activity-shell">
              <div className="att-activity-head d-none d-md-grid">
                <div>{t("app.attendance.hub.colActivity")}</div>
                <div>{t("app.attendance.hub.colTime")}</div>
                <div>{t("app.attendance.hub.colLocation")}</div>
                <div className="text-end">{t("app.attendance.hub.colPlatform")}</div>
              </div>
              {loading ? (
                <div className="p-4 text-center text-muted d-flex flex-column align-items-center justify-content-center gap-2">
                  <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                  <span>{t("app.common.loading")}</span>
                </div>
              ) : events.length === 0 ? (
                <div className="p-4 text-center text-muted">{t("app.attendance.hub.noEvents")}</div>
              ) : (
                [...events]
                  .sort(
                    (a, b) =>
                      (parseUtcFromApi(b.occurredAtUtc)?.getTime() ?? 0) -
                      (parseUtcFromApi(a.occurredAtUtc)?.getTime() ?? 0)
                  )
                  .map((ev) => (
                    <div key={ev.id || `${ev.occurredAtUtc}-${ev.eventType}`} className="att-activity-row">
                      <div className="att-activity-type">
                        <span
                          className={`att-activity-icon ${ev.eventType === AttendanceEventType.CheckOut ? "att-activity-icon--out" : "att-activity-icon--in"}`}
                        >
                          <i className={`bi ${ev.eventType === AttendanceEventType.CheckOut ? "bi-box-arrow-right" : "bi-box-arrow-in-right"}`} />
                        </span>
                        <span>{ev.eventType === AttendanceEventType.CheckOut ? t("app.attendance.hub.checkOut") : t("app.attendance.hub.checkIn")}</span>
                      </div>
                      <div className="small fw-semibold">{formatAttendanceTime(ev.occurredAtUtc)}</div>
                      <div className="small text-muted">{displayAttendancePlaceLabel(ev.location, ev.note) || "—"}</div>
                      <div className="text-md-end">
                        <span className="att-platform-pill">{deviceLabel(t, ev.clientDeviceType)}</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>

          <div>
            <h3 className="att-section-title px-1">{t("app.attendance.hub.workweekTitle")}</h3>
            <div className="att-workweek-card">
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="small fw-bold text-secondary">{t("app.attendance.hub.hoursCompleted")}</span>
                  <span className="small fw-bold d-flex align-items-center gap-2" style={{ color: "var(--att-accent-indigo)" }}>
                    {loading ? (
                      <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                    ) : (
                      <>
                        {formatHoursShort(displayTotal)}
                        <span className="text-muted fw-normal"> / 40.0</span>
                      </>
                    )}
                  </span>
                </div>
                <div className="att-progress-track">
                  <div
                    className="att-progress-fill"
                    style={{
                      width: loading ? "0%" : `${Math.min(100, (displayTotal / 40) * 100)}%`,
                      opacity: loading ? 0.35 : 1,
                    }}
                  />
                </div>
                <p className="small text-muted mt-3 mb-0 d-flex align-items-center gap-2">
                  <i className="bi bi-info-circle" style={{ color: "var(--att-accent-indigo)" }} />
                  {t("app.attendance.hub.workweekHint")}
                </p>
              </div>
              <div className="row g-3 mt-4 pt-4 border-top">
                {!hidePunctuality && (
                  <div className="col-6">
                    <div className="p-3 rounded-4 text-center h-100" style={{ background: "var(--att-surface-low)" }}>
                      <p className="att-metric-label mb-1">{t("app.attendance.hub.punctuality")}</p>
                      <p
                        className={`h4 fw-bold mb-0 ${
                          punctualityKind === "onTime" ? "text-success" : punctualityKind === "late" ? "text-warning" : "text-muted"
                        }`}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                        ) : punctualityKind === "onTime" ? (
                          t("app.attendance.hub.punctualityOnTime")
                        ) : punctualityKind === "late" ? (
                          t("app.attendance.hub.punctualityLate")
                        ) : (
                          "—"
                        )}
                      </p>
                    </div>
                  </div>
                )}
                <div className={hidePunctuality ? "col-12" : "col-6"}>
                  <div className="p-3 rounded-4 text-center h-100" style={{ background: "var(--att-surface-low)" }}>
                    <p className="att-metric-label mb-1">{t("app.attendance.hub.avgClockIn")}</p>
                    <p className="h4 fw-bold mb-0 d-flex align-items-center justify-content-center gap-2">
                      {loading ? (
                        <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden />
                      ) : firstCheckIn ? (
                        formatAttendanceTime(firstCheckIn.occurredAtUtc)
                      ) : (
                        "—"
                      )}
                    </p>
                  </div>
                </div>
              </div>
              {!hidePunctuality && (
                <p className="small text-muted mt-2 mb-0 px-1">{t("app.attendance.hub.punctualityHint")}</p>
              )}
              {!loading &&
                today &&
                (!today.allowUsersToEditSubmittedEvents || today.openCheckInSessionOnly) &&
                !bypassEmployeeEditDeleteOfficeFlags() && (
                <div className="att-alert-soft">
                  <span className="text-warning fs-4">
                    <i className="bi bi-exclamation-triangle-fill" />
                  </span>
                  <div>
                    <p className="fw-bold small mb-1">{t("app.attendance.hub.discrepancyTitle")}</p>
                    <p className="small text-muted mb-2">{t("app.attendance.hub.discrepancyBody")}</p>
                    <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => history.push("/attendance/submitted-requests")}>
                      {t("app.attendance.hub.discrepancyCta")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      <AttendanceClockModal
        open={modalOpen}
        onClose={() => !clockLoading && setModalOpen(false)}
        mode={modalMode}
        onConfirm={handleModalConfirm}
        projects={projects}
        loading={clockLoading}
        requireUserLocation={requireUserLocation}
      />

      {workplaceRequiredModal}
      <AttendanceHelpModal helpKey={helpKey} onClose={() => setHelpKey(null)} />
    </div>
  );
};

export default AttendanceHubPage;
